
import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Shield, Scale, ShieldCheck, Lock, Server, Award } from 'lucide-react';

// v2 ProyectMind logo (white version for dark footer)
const PMLogoWhite = () => (
  <div className="flex items-center gap-2.5">
    <svg width="30" height="30" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="32" height="32" rx="8" fill="rgba(124,58,237,0.7)"/>
      <rect x="7" y="7" width="8" height="8" rx="2.5" fill="white" fillOpacity="0.9"/>
      <rect x="17" y="7" width="8" height="8" rx="2.5" fill="#F59E0B"/>
      <rect x="7" y="17" width="8" height="8" rx="2.5" fill="#EC4899"/>
      <rect x="17" y="17" width="8" height="8" rx="2.5" fill="white" fillOpacity="0.25"/>
    </svg>
    <span
      style={{
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        fontWeight: 800,
        fontSize: '1.05rem',
        color: 'white',
        letterSpacing: '-0.03em',
      }}
    >
      ProyectMind
    </span>
  </div>
);

const Footer = () => {
  const { t, language, setLanguage } = useLanguage();

  return (
    <footer style={{ background: '#0F0E17', color: 'white' }}>
      <div className="container-custom py-16">
        {/* Main 4-col grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-14">
          {/* Brand column */}
          <div>
            <PMLogoWhite />
            <p
              className="mt-4 mb-5"
              style={{ color: '#64748B', fontSize: '0.88rem', fontFamily: 'Manrope, sans-serif', lineHeight: 1.7 }}
            >
              {t('footer.tagline')}
            </p>
            {/* LATAM flags */}
            <div className="flex flex-wrap gap-1.5 mb-5">
              {['🇲🇽', '🇨🇴', '🇦🇷', '🇨🇱', '🇵🇪', '🇪🇨', '🇺🇾'].map((flag) => (
                <span key={flag} className="text-base">{flag}</span>
              ))}
            </div>
            {/* Social links */}
            <div className="flex gap-3">
              <a href="https://twitter.com/proyectmind" target="_blank" rel="noopener noreferrer" aria-label="Twitter / X"
                className="transition-colors" style={{ color: '#475569' }}
                onMouseOver={e => (e.currentTarget.style.color = 'white')}
                onMouseOut={e => (e.currentTarget.style.color = '#475569')}
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M22 5.92a9.03 9.03 0 01-2.61.71 4.48 4.48 0 001.97-2.47 8.99 8.99 0 01-2.88 1.1A4.49 4.49 0 0015.88 4 4.5 4.5 0 0011.4 8.5c0 .35.04.69.11 1.02a12.76 12.76 0 01-9.25-4.7 4.46 4.46 0 001.4 6.01 4.46 4.46 0 01-2.05-.56v.06A4.5 4.5 0 005.5 14.8a4.56 4.56 0 01-2.03.08 4.5 4.5 0 004.2 3.13 9.02 9.02 0 01-5.59 1.93c-.36 0-.72-.02-1.07-.06A12.72 12.72 0 008.88 22c8.28 0 12.8-6.87 12.8-12.82 0-.2 0-.39-.01-.58A9.15 9.15 0 0022 5.92z"/>
                </svg>
              </a>
              <a href="https://linkedin.com/company/proyectmind" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"
                className="transition-colors" style={{ color: '#475569' }}
                onMouseOver={e => (e.currentTarget.style.color = 'white')}
                onMouseOut={e => (e.currentTarget.style.color = '#475569')}
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19 3a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h14m-.5 15.5v-5.3a3.26 3.26 0 00-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 011.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 001.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 00-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Producto */}
          <div>
            <h4
              className="mb-5"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: '0.88rem', color: 'white', letterSpacing: '0.06em', textTransform: 'uppercase' }}
            >
              Producto
            </h4>
            <ul className="space-y-3">
              {[
                { to: '/features', label: t('footer.features') },
                { to: '/integrations', label: t('footer.integrations') },
                { to: '/product/ai-predictive', label: t('footer.aiPredictive') },
                { to: '/use-cases', label: t('footer.useCases') },
                { to: '/pricing', label: t('nav.pricing') || 'Precios' },
              ].map(({ to, label }) => (
                <li key={to}>
                  <Link
                    to={to}
                    style={{ color: '#64748B', fontSize: '0.88rem', fontFamily: 'Manrope, sans-serif', textDecoration: 'none', transition: 'color 0.2s' }}
                    onMouseOver={e => (e.currentTarget.style.color = 'white')}
                    onMouseOut={e => (e.currentTarget.style.color = '#64748B')}
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Comparativas & Recursos */}
          <div>
            <h4
              className="mb-5"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: '0.88rem', color: 'white', letterSpacing: '0.06em', textTransform: 'uppercase' }}
            >
              Recursos
            </h4>
            <ul className="space-y-3">
              {[
                { to: '/blog', label: t('footer.blog') },
                { to: '/comparativas', label: t('footer.comparisons') },
                { to: '/comparativas/monday', label: 'vs Monday.com' },
                { to: '/comparativas/asana', label: 'vs Asana' },
                { to: '/comparativas/trello', label: 'vs Trello' },
                { to: '/diagrama-gantt-online-gratis', label: 'Gantt Online Gratis' },
                { to: '/user-guides', label: t('footer.userGuides') },
              ].map(({ to, label }) => (
                <li key={to}>
                  <Link
                    to={to}
                    style={{ color: '#64748B', fontSize: '0.88rem', fontFamily: 'Manrope, sans-serif', textDecoration: 'none', transition: 'color 0.2s' }}
                    onMouseOver={e => (e.currentTarget.style.color = 'white')}
                    onMouseOut={e => (e.currentTarget.style.color = '#64748B')}
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Empresa */}
          <div>
            <h4
              className="mb-5"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700, fontSize: '0.88rem', color: 'white', letterSpacing: '0.06em', textTransform: 'uppercase' }}
            >
              Empresa
            </h4>
            <ul className="space-y-3">
              {[
                { to: '/about-us', label: t('footer.aboutUs') },
                { to: '/contact', label: t('footer.contactUs') },
                { to: '/ai-ethics', label: 'Ética de IA' },
                { to: '/privacy-security', label: 'Seguridad y Privacidad' },
                { to: '/privacy-policy', label: t('footer.privacyPolicy') },
                { to: '/terms-of-service', label: t('footer.termsOfService') },
              ].map(({ to, label }) => (
                <li key={to}>
                  <Link
                    to={to}
                    style={{ color: '#64748B', fontSize: '0.88rem', fontFamily: 'Manrope, sans-serif', textDecoration: 'none', transition: 'color 0.2s' }}
                    onMouseOver={e => (e.currentTarget.style.color = 'white')}
                    onMouseOut={e => (e.currentTarget.style.color = '#64748B')}
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Compliance badges */}
        <div
          className="flex flex-wrap items-center justify-center gap-3 py-8 mb-8"
          style={{ borderTop: '1px solid rgba(255,255,255,0.08)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}
        >
          <Link to="/privacy-security" className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors"
            style={{ border: '1px solid rgba(6,182,212,0.3)', background: 'rgba(6,182,212,0.08)', color: '#06B6D4' }}>
            <Shield className="h-3.5 w-3.5" />GDPR Ready
          </Link>
          <Link to="/privacy-security" className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors"
            style={{ border: '1px solid rgba(16,185,129,0.3)', background: 'rgba(16,185,129,0.08)', color: '#10B981' }}>
            <Scale className="h-3.5 w-3.5" />Cumplimiento LFPDPPP
          </Link>
          <Link to="/ai-ethics" className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors"
            style={{ border: '1px solid rgba(124,58,237,0.3)', background: 'rgba(124,58,237,0.08)', color: '#A78BFA' }}>
            <Shield className="h-3.5 w-3.5" />IA Privada y Ética
          </Link>
          <div className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium"
            style={{ border: '1px solid rgba(16,185,129,0.3)', background: 'rgba(16,185,129,0.08)', color: '#10B981' }}>
            <ShieldCheck className="h-3.5 w-3.5" />SOC 2 Type II Audited
          </div>
          <div className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium"
            style={{ border: '1px solid rgba(245,158,11,0.3)', background: 'rgba(245,158,11,0.08)', color: '#F59E0B' }}>
            <Award className="h-3.5 w-3.5" />ISO 27001:2022
          </div>
        </div>

        {/* Bottom bar */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <p style={{ color: '#475569', fontSize: '0.82rem', fontFamily: 'Manrope, sans-serif' }}>
            © {new Date().getFullYear()} ProyectMind. {t('footer.allRightsReserved')}
          </p>

          {/* Language selector */}
          <div
            className="flex items-center rounded-full overflow-hidden"
            style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
          >
            <button
              onClick={() => setLanguage('es')}
              className="px-3 py-1.5 text-xs font-semibold rounded-full transition-all"
              style={{
                background: language === 'es' ? 'rgba(124,58,237,0.5)' : 'transparent',
                color: language === 'es' ? 'white' : '#64748B',
                fontFamily: 'Manrope, sans-serif',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              ES
            </button>
            <button
              onClick={() => setLanguage('en')}
              className="px-3 py-1.5 text-xs font-semibold rounded-full transition-all"
              style={{
                background: language === 'en' ? 'rgba(124,58,237,0.5)' : 'transparent',
                color: language === 'en' ? 'white' : '#64748B',
                fontFamily: 'Manrope, sans-serif',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              EN
            </button>
          </div>

          <div className="flex gap-5">
            <Link to="/privacy-policy" style={{ color: '#475569', fontSize: '0.8rem', fontFamily: 'Manrope, sans-serif', textDecoration: 'none' }}
              onMouseOver={e => (e.currentTarget.style.color = 'white')}
              onMouseOut={e => (e.currentTarget.style.color = '#475569')}
            >
              {t('footer.privacyPolicy')}
            </Link>
            <Link to="/terms-of-service" style={{ color: '#475569', fontSize: '0.8rem', fontFamily: 'Manrope, sans-serif', textDecoration: 'none' }}
              onMouseOver={e => (e.currentTarget.style.color = 'white')}
              onMouseOut={e => (e.currentTarget.style.color = '#475569')}
            >
              {t('footer.termsOfService')}
            </Link>
            <Link to="/cookie-policy" style={{ color: '#475569', fontSize: '0.8rem', fontFamily: 'Manrope, sans-serif', textDecoration: 'none' }}
              onMouseOver={e => (e.currentTarget.style.color = 'white')}
              onMouseOut={e => (e.currentTarget.style.color = '#475569')}
            >
              {t('footer.cookies')}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
